import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube } from "lucide-react";
import Logo from "../Logo";
import WhatsAppIcon from "../icons";
import DonateButton from "../DonateButton";
import { useLang } from "../../i18n";
import { useContent } from "../../lib/useContent";

export default function Footer() {
  const { t, lang, pick } = useLang();
  const c = useContent();
  const socials = [
    { icon: Facebook, label: "Facebook", url: c.social.facebook, testid: "social-facebook" },
    { icon: Instagram, label: "Instagram", url: c.social.instagram, testid: "social-instagram" },
    { icon: Youtube, label: "YouTube", url: c.social.youtube, testid: "social-youtube" },
    { icon: WhatsAppIcon, label: "WhatsApp", url: c.social.whatsapp ? `https://wa.me/${c.social.whatsapp}` : "", testid: "social-whatsapp" },
  ];
  const workLinks = lang === "hi"
    ? ["शिक्षा", "स्वास्थ्य", "महिला सशक्तिकरण", "पर्यावरण", "समाज सेवा"]
    : ["Education", "Healthcare", "Women Empowerment", "Environment", "Social Service"];

  return (
    <footer data-testid="footer" className="bg-ngo-forest text-ngo-mint relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-ngo-deep/40 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 relative">
        <div data-testid="footer-donate-band" className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 bg-ngo-deep/50 border border-ngo-mint/15 rounded-3xl px-8 py-7">
          <div>
            <h3 className="font-heading font-bold text-xl text-ngo-cream">
              {pick({ hi: "आपका सहयोग, किसी की बदलती ज़िंदगी", en: "Your support, someone's changing life" })}
            </h3>
            <p className="text-sm text-ngo-mint/70 mt-1.5">
              {pick({ hi: "शिक्षा, स्वास्थ्य और समाज सेवा की इस पहल में अपना योगदान दें।", en: "Contribute to this initiative of education, health and social service." })}
            </p>
          </div>
          <DonateButton label={pick({ hi: "दान करें", en: "Donate Now" })} variant="gold" size="lg" testid="footer-donate-btn" className="shrink-0" />
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 relative">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <Logo size={46} />
            <div>
              <p className="font-heading font-bold text-lg text-ngo-cream">{lang === "hi" ? c.org_name_hi : c.org_name_en}</p>
              <p className="text-[10px] tracking-widest uppercase text-ngo-gold">ANANSHA WELFARE SOCIETY</p>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-ngo-mint/75">{t("footer.about")}</p>
          <p className="mt-4 text-xs text-ngo-mint/60">{t("top.reg")}: {c.reg_no}</p>
        </div>

        <nav aria-label="फुटर लिंक">
          <h3 className="font-heading font-semibold text-ngo-cream mb-5">{t("footer.quick")}</h3>
          <ul className="space-y-2.5 text-sm">
            {[
              { to: "/", k: "nav.home" }, { to: "/about", k: "nav.about" }, { to: "/our-work", k: "nav.work" },
              { to: "/campaigns", k: "nav.campaigns" }, { to: "/gallery", k: "nav.gallery" }, { to: "/events", k: "nav.events" },
              { to: "/blog", k: "nav.blog" }, { to: "/reports", k: "nav.reports" }, { to: "/founder", k: "nav.founder" }, { to: "/videos", k: "nav.videos" }, { to: "/contact", k: "nav.contact" },
            ].map((l) => (
              <li key={l.k}>
                <Link data-testid={`footer-${l.k}`} to={l.to} className="text-ngo-mint/75 hover:text-ngo-gold transition-colors duration-200">
                  {t(l.k)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h3 className="font-heading font-semibold text-ngo-cream mb-5">{t("footer.work")}</h3>
          <ul className="space-y-2.5 text-sm">
            {workLinks.map((w) => (
              <li key={w}>
                <Link to="/our-work" className="text-ngo-mint/75 hover:text-ngo-gold transition-colors duration-200">{w}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-heading font-semibold text-ngo-cream mb-5">{t("footer.contact")}</h3>
          <ul className="space-y-3 text-sm text-ngo-mint/75">
            <li className="flex items-start gap-2.5">
              <Phone size={15} className="mt-0.5 shrink-0 text-ngo-gold" />
              <a data-testid="footer-phone" href={`tel:${c.phone.replace(/\s/g, "")}`} className="hover:text-ngo-gold transition-colors duration-200">{c.phone}</a>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail size={15} className="mt-0.5 shrink-0 text-ngo-gold" />
              <a data-testid="footer-email" href={`mailto:${c.email}`} className="hover:text-ngo-gold transition-colors duration-200 break-all">{c.email}</a>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin size={15} className="mt-0.5 shrink-0 text-ngo-gold" />
              <span>{lang === "hi" ? c.address_hi : c.address_en}</span>
            </li>
          </ul>
          <div className="flex gap-3 mt-6">
            {socials.map((s) => (
              <a
                key={s.label}
                data-testid={s.testid}
                href={s.url || "#"}
                target={s.url ? "_blank" : undefined}
                rel="noopener noreferrer"
                aria-label={s.label}
                className={`w-9 h-9 rounded-full border border-ngo-mint/25 flex items-center justify-center transition-colors duration-200 ${
                  s.url ? "hover:bg-ngo-gold hover:text-ngo-forest hover:border-ngo-gold" : "opacity-40 cursor-default"
                }`}
              >
                <s.icon size={15} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-ngo-mint/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ngo-mint/60">
          <p>{t("footer.rights")}</p>
          <p className="flex items-center gap-4">
            <Link data-testid="footer-privacy" to="/privacy-policy" className="hover:text-ngo-gold transition-colors duration-200">{t("footer.privacy")}</Link>
            <span className="opacity-40">|</span>
            <Link data-testid="footer-terms" to="/terms" className="hover:text-ngo-gold transition-colors duration-200">{t("footer.terms")}</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
